// src/components/common/actionBeat/FillButtonContent.js
//
// Shared FILL visual: the translucent progress overlay (sweep -> crawl ->
// complete) plus the three stacked labels (idle/working/success) that
// cross-fade in place, with a drawn-in check on success. Extracted from
// ActionBeatFill.js (WAVE 1 · ENGINE, 2026-09-24) so ConfirmMorphActions can
// reuse the IDENTICAL visual once the two-tap capsule itself starts filling
// on commit — same math, same markup, no drift between the two footers.
//
// Callers own the PHASE/STAGE state: the mount-in-working trick (ActionBeatFill,
// for a drawer that swaps its own footer already mid-save) and the plain
// prop-driven transition (ConfirmMorphActions, which is continuously mounted
// from rest onward) differ per caller, so this component stays a pure render
// of already-resolved values — no hooks, no timers.

import { FILL_MS, BEAT_EASE } from '../../../ui/actionBeatMotion';
import { foundation } from '../../../ui';
import { alphaOf } from '../../../utils/ui/colorAlpha';
import { status } from '../../../ui/theme/semanticTokens';

const ORDER = { idle: 0, working: 1, success: 2 };
export const SEQ_OUT_MS = 80;
export const SEQ_IN_MS = 110;

export function DrawnCheck({ size, strokeWidth, drawn, ms, delayMs, reduced }) {
  // Zero layout contribution (KAN-768): the glyph's margin box is 0 tall (half
  // the size pulled in on each side; the flex row centres the margin box, so
  // the glyph stays centred) -- it can never make the button taller than its
  // idle text line when the success label appears.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={foundation.color.brand.onPrimary}
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden
      style={{ flexShrink: 0, margin: `${-size / 2}px 0` }} data-fill-check>
      <path d="M20 6 9 17l-5-5" style={{
        strokeDasharray: 24,
        strokeDashoffset: drawn ? 0 : 24,
        transition: reduced ? 'stroke-dashoffset 120ms ease' : `stroke-dashoffset ${ms}ms ${BEAT_EASE.out} ${delayMs}ms`,
      }} />
    </svg>
  );
}

/** Each label sits at its own phase slot; not-yet-shown waits below (+8),
 * already-shown exits upward (-8) — the sequence never reverses. */
export function labelStyle(labelPhase, active, reduced, ellipsize = false, sequential = false) {
  const cur = ORDER[active];
  const own = ORDER[labelPhase];
  const shown = own === cur;
  // Default: the outgoing and incoming labels cross-fade TOGETHER (both partly
  // legible for ~160ms). `sequential` (opt-in, KAN-772): the outgoing label
  // fades out fully (SEQ_OUT_MS) BEFORE the incoming one starts (delay >=
  // SEQ_OUT_MS), so two legible labels are never on screen at once.
  let transition = `opacity ${FILL_MS.labelSwap}ms ease, transform 220ms ${BEAT_EASE.out}`;
  if (sequential) {
    transition = shown
      ? `opacity ${SEQ_IN_MS}ms ease ${SEQ_OUT_MS + 10}ms, transform 220ms ${BEAT_EASE.out} ${SEQ_OUT_MS + 10}ms`
      : `opacity ${SEQ_OUT_MS}ms ease, transform 220ms ${BEAT_EASE.out}`;
  }
  return {
    gridArea: '1 / 1', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    opacity: shown ? 1 : 0,
    transform: shown ? 'none' : `translateY(${own < cur ? -8 : 8}px)`,
    transition: reduced ? 'none' : transition,
    ...(ellipsize === 'clip' ? { minWidth: 0 } : null),
    ...(ellipsize === true ? { minWidth: 0, overflow: 'hidden' } : null),
  };
}

// Opt-in (`ellipsize`): a fixed-width button (the full-screen action row's
// primary slot) keeps a long label inside its box with "…" instead of
// widening or clipping both ends. Default off, so every other consumer is
// untouched. `ellipsize="clip"` (KAN-768) keeps the same boxes but centres the
// label and lets the button clip BOTH ends with no "..." -- used while a
// button shrinks back after a beat, so the label never flashes an ellipsis.
const ellipsisText = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const clipText = { flexShrink: 0, whiteSpace: 'nowrap' };
const ellipsisStatus = { position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', minWidth: 0 };

export function fillWidth({ active, stage, reduced }) {
  if (active === 'success') {
    return { width: '100%', opacity: 0, transition: reduced ? 'width 120ms ease, opacity 120ms ease' : `width ${FILL_MS.complete}ms ease, opacity 200ms ease 160ms` };
  }
  // At rest (idle) the overlay is always empty - a failed save returns here and must not leave a strip behind.
  if (active === 'idle') return { width: 0, opacity: 1, transition: 'none' };
  if (reduced) return { width: `${FILL_MS.sweepTo * 100}%`, opacity: 1, transition: 'width 120ms ease' };
  if (stage === 'crawl') return { width: `${FILL_MS.crawlTo * 100}%`, opacity: 1, transition: `width ${FILL_MS.crawl}ms linear` };
  if (stage === 'run') return { width: `${FILL_MS.sweepTo * 100}%`, opacity: 1, transition: `width ${FILL_MS.sweep}ms ${BEAT_EASE.sweep}` };
  return { width: 0, opacity: 1, transition: 'none' };
}

/** The confirm button's FINISH colour on success — delete/security tones land
 * neutral (nothing to celebrate), save lands the semantic success green. */
export function finishStyle(tone) {
  const c = tone === 'save' ? status.finishSave : status.finishNeutral;
  return { background: c, backgroundColor: c, border: `1px solid ${c}` };
}

/**
 * `phase` = the RESOLVED display phase ('idle' | 'working' | 'success');
 * `stage` = sweep staging ('start' | 'run' | 'crawl'), ignored once phase is
 * 'success'. Renders two children meant to sit INSIDE a `position: relative`
 * button — the overlay is absolutely positioned, the label stack is a
 * relatively-positioned grid so all three labels can cross-fade in place.
 */
export default function FillButtonContent({ phase, stage, reduced, confirmLabel, workingLabel, successLabel, ellipsize = false, sequentialLabels = false }) {
  const wrap = (text) => {
    if (!ellipsize) return text;
    return <span style={ellipsize === 'clip' ? clipText : ellipsisText}>{text}</span>;
  };
  const fill = fillWidth({ active: phase, stage, reduced });
  return (
    <>
      <span aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, background: alphaOf(foundation.color.brand.onPrimary, 0.22), ...fill }} />
      <span role="status" aria-live="polite" style={ellipsize ? ellipsisStatus : { position: 'relative', display: 'inline-grid' }}>
        {/* Only the CURRENT label is exposed: all three stay mounted for the
            cross-fade, and without aria-hidden the button's accessible name
            would read "Remove Removing… Removed". */}
        <span aria-hidden={phase !== 'idle' || undefined} style={labelStyle('idle', phase, reduced, ellipsize, sequentialLabels)}>{wrap(confirmLabel)}</span>
        <span aria-hidden={phase !== 'working' || undefined} style={labelStyle('working', phase, reduced, ellipsize, sequentialLabels)}>{wrap(workingLabel)}</span>
        <span aria-hidden={phase !== 'success' || undefined} style={labelStyle('success', phase, reduced, ellipsize, sequentialLabels)}>
          <DrawnCheck size={16} strokeWidth={2.75} drawn={phase === 'success'} ms={FILL_MS.tick} delayMs={FILL_MS.tickDelay} reduced={reduced} />
          {wrap(successLabel)}
        </span>
      </span>
    </>
  );
}
